import { useEffect, useState, type FC } from 'react';
import { Button, Space, Textarea, Text } from '@g2u/ui-kit';
import { useMutation } from '@tanstack/react-query';
import { Form } from 'antd';

import { useServiceProductsUpdate, useServicePartsUpdate, useServicePrice, useServiceDiscountsUpdate } from '../hooks';
import { ServiceList, ServiceTotal, PartsList, DiscountList, LegacyServices } from '.';
import { putServiceAttributes } from 'queries';
import { Toast, parseErrors, useJob } from 'helpers';
import type { JsonDataItem, ServiceProduct } from 'types';
import styles from '../edit.module.scss';
import { calculateTotalHours } from 'helpers/hooks/useTotalHours';
import { useServiceProducts } from '../hooks/useServiceProducts';
import { useProductList } from 'helpers/hooks/useProductList';
import { omit } from 'lodash';
import { isValid, parse } from 'date-fns';

type SelectedProduct = Partial<ServiceProduct> & { productId: ServiceProduct['productId'] };

enum JobSource {
  LEGACY = 'legacy',
  BMSV3 = 'bmsv3',
}

type Props = {
  jobId?: string;
  isLocked: boolean;
};

type FormFields = {
  description: string;
  products?: SelectedProduct[];
  parts: JsonDataItem;
  discounts: JsonDataItem;
  legacy_products: JsonDataItem;
};

export const ServiceDetails: FC<Props> = (props) => {
  const { jobId, isLocked } = props;
  const { job, refetchService } = useJob(jobId);
  const {
    updateLegacyProducts,
    isLoading: isLegacyProductsUpdating,
    isError: isLegacyError,
  } = useServiceProductsUpdate();
  const { updateServiceParts, isLoading: isPartsUpdating, isError: isPartsUpdateError } = useServicePartsUpdate();
  const {
    updateServiceDiscounts,
    isLoading: isDiscountUpdating,
    isError: isDiscountUpdateError,
  } = useServiceDiscountsUpdate();
  const { updateServicePrice, servicePrice } = useServicePrice(job?.customer?.ID.toString(), job?.ID.toString());
  const [isJobUpdating, setIsJobUpdating] = useState<boolean>(false);
  const [form] = Form.useForm<FormFields>();
  const serviceDescription = job?.attributes?.find(
    (attribute: JsonDataItem) => attribute.key === 'technician_report',
  )?.value;
  const isLegacySource = job?.source === JobSource.LEGACY;
  const isLegacyEnabled = job?.support_legacy === true;
  const isClosed = job?.status && ['cancelled', 'completed'].includes(job?.status);
  const values = Form.useWatch([], form);

  const products = values?.products;

  const [productList] = useProductList();

  const totalHours = calculateTotalHours(
    products?.map((sp) => ({
      qty: sp.quantity?.toString(),
      details: {
        product: productList?.find((p) => p.ID === sp.productId),
      },
    })),
  );

  const {
    mutate: updateDescription,
    isLoading: isAttributesLoading,
    isError: isDescriptionUpdateError,
  } = useMutation(putServiceAttributes, {
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const [serviceProducts, isLoadingServiceProducts, updateServiceProducts, isUpdatingServiceProducts, updateError] =
    useServiceProducts(job?.ID);

  const [productsAndPartsTriggered, setProductsAndPartsTriggered] = useState(false);
  const handleUpdateJobDetails = () => {
    const serviceId = job?.ID;

    if (isLegacyEnabled) {
      updateLegacyProducts({
        serviceId,
        initialData: { job: job?.job, consultations: job?.jobs_labour },
        newData: form.getFieldValue('legacy_products'),
      });
    } else {
      products &&
        updateServiceProducts?.(products.map((p) => omit(p, ['product', 'createdAt', 'updatedAt', 'createdBy'])));
    }

    updateServiceParts({
      serviceId,
      initialParts: job?.service_parts,
      newParts: form.getFieldValue('parts'),
    });

    setProductsAndPartsTriggered(true);
  };

  const productsAndPartsComplete = !(isPartsUpdating || isLegacyProductsUpdating || isLoadingServiceProducts);

  useEffect(() => {
    const isDescriptionChanged = serviceDescription !== form.getFieldValue('description');
    const serviceId = job?.ID;

    if (productsAndPartsTriggered && productsAndPartsComplete) {
      setProductsAndPartsTriggered(false);
      updateServiceDiscounts({
        serviceId,
        newDiscounts: form.getFieldValue('discounts'),
      });
      isDescriptionChanged &&
        serviceId &&
        updateDescription({
          serviceId,
          attributes: [
            {
              field: 'technician_report',
              value: form.getFieldValue('description'),
            },
          ],
        });
    }
  }, [productsAndPartsTriggered, productsAndPartsComplete]);

  const handleRecalculateCost = () => {
    const formData = form.getFieldsValue(['legacy_products', 'products', 'parts', 'discounts']);
    updateServicePrice({ ...formData, job });
  };

  const isMutating =
    isDiscountUpdating ||
    isPartsUpdating ||
    isLegacyProductsUpdating ||
    isAttributesLoading ||
    isUpdatingServiceProducts;
  const isUpdateError =
    isDiscountUpdateError || isDescriptionUpdateError || isLegacyError || !!updateError || isPartsUpdateError;

  useEffect(() => {
    isMutating && setIsJobUpdating(true);

    if (!isMutating && isJobUpdating && !productsAndPartsTriggered) {
      setIsJobUpdating(false);

      if (!isUpdateError) {
        refetchService();
        Toast.show({ description: 'The service details have been updated', type: Toast.Type.Success });
        form.setFields(
          (['description', 'products', 'parts', 'discounts', 'legacy_products'] as const).map((name) => ({
            name,
            touched: false,
          })),
        );
      }
    }
  }, [isMutating, isJobUpdating, isUpdateError, productsAndPartsTriggered]);

  // Set products initial value async as it has to be fetched
  useEffect(() => {
    if (!isLoadingServiceProducts) {
      form.setFields([{ name: 'products', value: serviceProducts, touched: false }]);
    }
  }, [isLoadingServiceProducts, serviceProducts]);

  // FIXME: This shouldn't need an effect, just recalculate on render
  useEffect(() => {
    handleRecalculateCost();
  }, [products, serviceProducts]);

  return (
    <>
      <title>Update Service Details</title>
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large}>
        <Form
          form={form}
          name="service-details-form"
          layout="horizontal"
          onFinish={handleUpdateJobDetails}
          colon={false}
          preserve={true}
          disabled={isLocked}
        >
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
            <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
              {isLegacyEnabled ? (
                // TODO: remove when legacy bms jobs are not supported
                <LegacyServices
                  isLegacySource={isLegacySource}
                  job={job}
                  onSelect={(productValue) => {
                    form.setFields([{ name: 'legacy_products', value: productValue, touched: true }]);
                    handleRecalculateCost();
                  }}
                  form={form}
                  totalPrice={servicePrice.legacyProductsTotal}
                />
              ) : job ? (
                <ServiceList
                  serviceId={jobId}
                  customerId={job.customer.ID}
                  isRequired={true}
                  onSelect={(productValue: SelectedProduct[]) => {
                    form.setFields([{ name: 'products', value: productValue, touched: true }]);
                  }}
                  totalPrice={servicePrice.productsTotal}
                  selectedProducts={products}
                  totalHours={totalHours}
                  isGeekCover={job?.customer.is_geek_cover}
                  isClosed={['cancelled', 'completed'].includes(job?.status)}
                  serviceDate={
                    job?.service_appointment &&
                    isValid(parse(job.service_appointment, 'yyyy-MM-dd HH:mm:ss', new Date()))
                      ? parse(job?.service_appointment, 'yyyy-MM-dd HH:mm:ss', new Date())
                      : null
                  }
                  serviceType={job?.attributes.find((a) => a?.key === 'job_service_type')?.value || 'N'}
                />
              ) : null}
            </Space.$>
            <PartsList
              isLocked={isLocked}
              isClosed={['cancelled', 'completed'].includes(job?.status || '')}
              serviceId={jobId}
              partsSelection={job?.service_parts?.map((item: JsonDataItem) => {
                return {
                  id: item.ID,
                  qty: Number(item.qty).toFixed(2),
                  price: Number(item.total_price).toFixed(2),
                  description: item.description,
                  type: item.inventoryitem?.type,
                  details: item,
                };
              })}
              onSelect={(partValue) => {
                form.setFields([{ name: 'parts', value: partValue, touched: true }]);
                handleRecalculateCost();
              }}
              totalPrice={servicePrice.partsTotal}
            />
            <DiscountList
              onSelect={(discountValue) => {
                form.setFields([{ name: 'discounts', value: discountValue, touched: true }]);
                handleRecalculateCost();
              }}
              job={job}
              totalPrice={servicePrice.discountTotal}
            />
            <ServiceTotal data={servicePrice} totalHours={totalHours} />
            <Text.$ strong size={Text.Size.LG}>
              Service Description
            </Text.$>
            <Textarea.$
              disabled={isClosed || isLocked}
              initialValue={serviceDescription}
              className={styles.jobEdit__field}
              name="description"
              placeholder="Please describe what actions were taken to complete the service"
            />
          </Space.$>
        </Form>

        <Button.$
          type={Button.Type.Primary}
          dataTestId="update-service-details-btn"
          disabled={!form.isFieldsTouched() || isLocked}
          onClick={() => {
            form.submit();
          }}
          style={{ float: 'right' }}
          loading={isJobUpdating}
        >
          Save
        </Button.$>
      </Space.$>
    </>
  );
};
